/*
 * Copyright (C) 2026 - present Marek Kuzora - All Rights Reserved.
 */


const EPS = 1e-10;


export const add = (lhs, rhs) =>
{
  return { x: lhs.x + rhs.x, y: lhs.y + rhs.y };
}


export const subtract = (lhs, rhs) =>
{
  return { x: lhs.x - rhs.x, y: lhs.y - rhs.y };
}


export const scale = (point, scale) =>
{
  return { x: point.x * scale, y: point.y * scale };
}


export const transform = (point, offset, angle) =>
{
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);

  return { x: offset.x + point.x * cos - point.y * sin, y: offset.y + point.x * sin + point.y * cos };
}


export const dotProduct = (vectorA, vectorB) =>
{
  return vectorA.x * vectorB.x + vectorA.y * vectorB.y;
}


export const distance = (lhs, rhs) =>
{
  const x = lhs.x - rhs.x;
  const y = lhs.y - rhs.y;

  return Math.sqrt(x * x + y * y);
}


export const areEqual = (pointA, pointB) =>
{
  return Math.abs(pointA.x - pointB.x) < EPS && Math.abs(pointA.y - pointB.y) < EPS;
}


// TODO Can generalize to any point on line and any angle - not only mid-point -Math.PI/2 angle!
export const getPerpendicularBisector = (lhs, rhs, offset) =>
{
  const mid = scale(add(lhs, rhs), 0.5);

  const theta = getLineAngleRadians(lhs, rhs) - Math.PI / 2;

  return { x: mid.x + offset * Math.cos(theta), y: mid.y + offset * Math.sin(theta) };
}


export const getLineAngleRadians = (lhs, rhs) =>
{
  return Math.atan2(rhs.y - lhs.y, rhs.x - lhs.x);
}


export const getLineAngleDegrees = (lhs, rhs) =>
{
  return getLineAngleRadians(lhs, rhs) * 180 / Math.PI
}


export const getPointWithOffset = (lhs, rhs, offset) =>
{
  const length = (offset < 0) ? distance(lhs, rhs) + offset : offset;

  return getPointWithScale(lhs, rhs, length / distance(lhs, rhs));
}


export const getPointWithScale = (lhs, rhs, ratio) =>
{
  return add(scale(subtract(lhs, rhs), ratio), rhs);
}


export const getIntersectionPoint = (pointA, pointB, pointC, pointD) =>
{
  const pointAB = subtract(pointA, pointB);
  const pointCD = subtract(pointC, pointD);

  const c = pointAB.x * pointCD.y - pointAB.y * pointCD.x;

  if (Math.abs(c) < EPS) // TODO better handle precision
  {
    return null;
  }

  const a = pointA.x * pointB.y - pointA.y * pointB.x;
  const b = pointC.x * pointD.y - pointC.y * pointD.x;

  const x = (a * pointCD.x - b * pointAB.x) / c;
  const y = (a * pointCD.y - b * pointAB.y) / c;

  return { x, y };
}


export const getIntersectionPointWithin = (pointA, pointB, pointC, pointD) =>
{
  const intersection = getIntersectionPoint(pointA, pointB, pointC, pointD);

  if (intersection == null)
  {
    return null;
  }

  const insideABx = intersection.x >= Math.min(pointA.x, pointB.x) - EPS && intersection.x <= Math.max(pointA.x, pointB.x) + EPS;
  const insideABy = intersection.y >= Math.min(pointA.y, pointB.y) - EPS && intersection.y <= Math.max(pointA.y, pointB.y) + EPS;

  const insideCDx = intersection.x >= Math.min(pointC.x, pointD.x) - EPS && intersection.x <= Math.max(pointC.x, pointD.x) + EPS;
  const insideCDy = intersection.y >= Math.min(pointC.y, pointD.y) - EPS && intersection.y <= Math.max(pointC.y, pointD.y) + EPS;

  return (insideABx && insideABy && insideCDx && insideCDy) ? intersection : null
}


export const getPerpendicularIntersection = (pointA, pointB, pointC) =>
{
  const pointAB = subtract(pointA, pointB);
  const pointCA = subtract(pointC, pointA);

  const projection = dotProduct(pointCA, pointAB) / dotProduct(pointAB, pointAB);

  return add(pointA, scale(pointAB, projection));
};


export const getPerpendicularIntersectionAlternate = (pointA, pointB, pointC) =>
{
  const thetaAB = getLineAngleRadians(pointA, pointB);

  const dx = Math.cos(thetaAB + Math.PI / 2);
  const dy = Math.sin(thetaAB + Math.PI / 2);

  return getIntersectionPoint(pointA, pointB, pointC, { x: pointC.x + dx, y: pointC.y + dy });
}


export const getJaggedPoints = (length, step, amplitude) =>
{
  const count = getJaggedCount(length, step);

  const points = [ { x: 0, y: 0 } ];

  for (let index = 1; index < count; ++index)
  {
    points.push({ x: index * length / count, y: (index % 2 ? 1 : -1) * amplitude });
  }

  return [ ...points, { x: length, y: 0 } ];
}


export const getJaggedCount = (length, step) =>
{
  return Math.max(2, Math.round(length / step));
}
